import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../Modal';
import { Button } from '../Button';
import { DependencyTreeView } from './DependencyTreeView';
import type { Dependency, CascadeDeleteProgress, DeletionStageInfo } from '../../../types/cascadeDelete';

export interface CascadeDeleteDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Type of entity being deleted */
  entityType: 'partner' | 'connection';
  /** Name of the entity being deleted */
  entityName: string;
  /** List of dependent resources */
  dependencies: Dependency[];
  /** Whether deletion is in progress */
  isDeleting?: boolean;
  /** Deletion progress information */
  deletionProgress?: CascadeDeleteProgress;
}

/**
 * Warning triangle icon
 */
const WarningIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-12 h-12 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/**
 * Checkmark icon for completed stages
 */
const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Loading spinner for active stages
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-4 h-4 animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Error/X icon for failed stages
 */
const ErrorIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Progress indicator component showing deletion stages
 */
const DeletionProgressIndicator: React.FC<{ progress: CascadeDeleteProgress }> = ({
  progress,
}) => {
  // Filter out stages that don't apply (e.g., no tasks to delete)
  const visibleStages = progress.stages.filter(
    (stage) =>
      stage.stage !== 'completed' &&
      stage.stage !== 'failed' &&
      (stage.isComplete || stage.isActive || stage.stage === 'preparing')
  );

  const getStageIcon = (stage: DeletionStageInfo) => {
    if (stage.error) {
      return <ErrorIcon className="text-danger-500" />;
    }
    if (stage.isComplete) {
      return <CheckIcon className="text-success-500" />;
    }
    if (stage.isActive) {
      return <SpinnerIcon className="text-primary-500" />;
    }
    return (
      <div className="w-4 h-4 rounded-full border-2 border-neutral-300 dark:border-neutral-600" />
    );
  };

  const getStageTextClass = (stage: DeletionStageInfo) => {
    if (stage.error) {
      return 'text-danger-600 dark:text-danger-400';
    }
    if (stage.isComplete) {
      return 'text-success-600 dark:text-success-400';
    }
    if (stage.isActive) {
      return 'text-primary-600 dark:text-primary-400 font-medium';
    }
    return 'text-neutral-400 dark:text-neutral-500';
  };

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="relative pt-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {progress.message}
          </span>
          <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
            {progress.percentage}%
          </span>
        </div>
        <div className="overflow-hidden h-2 rounded-full bg-neutral-200 dark:bg-neutral-700">
          <div
            className={[
              'h-full rounded-full transition-all duration-500 ease-out',
              progress.error
                ? 'bg-danger-500'
                : 'bg-gradient-to-r from-primary-500 to-primary-600',
            ].join(' ')}
            style={{ width: `${progress.percentage}%` }}
            role="progressbar"
            aria-valuenow={progress.percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Stage list */}
      <div className="space-y-2">
        {visibleStages.map((stage, index) => (
          <div
            key={stage.stage}
            className={[
              'flex items-center gap-3 py-1.5',
              index > 0 ? 'border-t border-neutral-100 dark:border-neutral-700/50' : '',
            ].join(' ')}
          >
            {getStageIcon(stage)}
            <span className={`text-sm ${getStageTextClass(stage)}`}>
              {stage.label}
              {stage.currentCount !== undefined &&
                stage.totalCount !== undefined &&
                ` (${stage.currentCount}/${stage.totalCount})`}
            </span>
          </div>
        ))}
      </div>

      {/* Error message */}
      {progress.error && (
        <div className="mt-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
          <p className="text-sm text-danger-700 dark:text-danger-300">{progress.error}</p>
        </div>
      )}
    </div>
  );
};

/**
 * CascadeDeleteDialog Component
 *
 * Modal dialog for confirming cascade delete operations.
 * Shows a warning about cascade deletion, lists all dependent resources,
 * and provides a progress indicator during deletion.
 */
export const CascadeDeleteDialog: React.FC<CascadeDeleteDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  entityType,
  entityName,
  dependencies,
  isDeleting = false,
  deletionProgress,
}) => {
  const entityTypeLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);
  const dependencyCount = dependencies.length;
  const hasProgress = isDeleting && deletionProgress;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={!isDeleting}
      closeOnEscape={!isDeleting}
      showCloseButton={!isDeleting}
      ariaLabel={`Delete ${entityTypeLabel} confirmation`}
    >
      <ModalHeader>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 rounded-full bg-danger-100 dark:bg-danger-900/30">
            <WarningIcon className="text-danger-600 dark:text-danger-400" />
          </div>
          <div className="flex-1 pt-1">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Delete {entityTypeLabel}?
            </h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              This action cannot be undone
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody padding="md">
        {hasProgress ? (
          <DeletionProgressIndicator progress={deletionProgress} />
        ) : (
          <div className="space-y-4">
            {/* Warning message */}
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <p>
                You are about to delete{' '}
                <span className="font-semibold text-neutral-900 dark:text-white">
                  "{entityName}"
                </span>
                .
                {dependencyCount > 0 && (
                  <>
                    {' '}
                    This {entityType} has{' '}
                    <span className="font-semibold text-danger-600 dark:text-danger-400">
                      {dependencyCount}
                    </span>{' '}
                    dependent resource{dependencyCount !== 1 ? 's' : ''} that will also be
                    deleted.
                  </>
                )}
              </p>
            </div>

            {/* Dependency tree */}
            {dependencyCount > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                  The following resources will be permanently deleted:
                </h3>
                <DependencyTreeView
                  dependencies={dependencies}
                  defaultExpanded={dependencyCount <= 20}
                  maxVisibleItems={5}
                />
              </div>
            )}

            {/* Final warning */}
            <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
              <p className="text-sm text-danger-700 dark:text-danger-300">
                <strong>Warning:</strong> This action cannot be undone. All associated AWS
                resources (buckets, keys, connectors) will also be cleaned up.
              </p>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter align="right">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isDeleting}
          isLoading={isDeleting}
        >
          {isDeleting
            ? 'Deleting...'
            : `Delete ${entityType}${dependencyCount > 0 ? ` and ${dependencyCount} resource${dependencyCount !== 1 ? 's' : ''}` : ''}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CascadeDeleteDialog.displayName = 'CascadeDeleteDialog';

export default CascadeDeleteDialog;
